import { IconButton } from '@mui/material';
import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FileUpload, { FilesPropType } from 'src/components/FileUpload';
import Typography from 'src/components/Typography';
import { setLocationParams } from 'src/resources/location/reducer';
import { FileType } from 'src/types/file';
import { RootStoreType } from 'src/types/store';
import downloadFile from 'src/utils/downloadFile';
// import { useCheckPermission } from 'src/utils/checkPermission';
import getBackUrl from 'src/utils/getBackUrl';
import ColumnOptions from './ColumnOptions';
import DownloadIcon from '@mui/icons-material/Download';
import Tooltip from 'src/components/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import * as XLSX from 'xlsx';
import { formatData } from './functions';
import Loading from 'src/components/Loading/intex';
import { setReceivingDetailData } from 'src/resources/receivingDetail/reducer';
import { useNavigate } from 'react-router-dom';
import api from '@api';
import { EDIT_KEYS } from '../ReceivingDetailContainer/constants';
import { ReceivingType } from 'src/types/receiving';

function mergeReceivingFromExcel(apiReceiving: ReceivingType, excelData: ReceivingType): ReceivingType {
    const merged: ReceivingType = { ...apiReceiving };
    (EDIT_KEYS as readonly (keyof ReceivingType)[]).forEach((key) => {
        if (excelData[key] !== undefined) {
            (merged as Record<string, unknown>)[key] = excelData[key];
        }
    });
    return merged;
}

const ReceivingImportContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const formRef = useRef<HTMLFormElement>(null);

    const locationHistory = useSelector((state: RootStoreType) => state.location.history);

    // Load constants for column options
    const handlingUnitsCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.handlingUnitsCodes);
    const receivingDeliveryTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingDeliveryTypeCodes);
    const driverCompanyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.driverCompanyCodes);
    const receivingTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingTypeCodes);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const incotermsCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.incotermsCodes);

    const [inputFiles, setInputFiles] = useState<FileType[]>([]);
    const [showColumnOptions, setShowColumnOptions] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const backUrl = getBackUrl(locationHistory, '/products/active/');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('receivingImport.title'), backUrl }));
    }, [backUrl, locationHistory]);

    const loadFileData = (file: FileType) => {
        const reader = new FileReader();
        // @ts-ignore
        reader.readAsBinaryString(file);
        reader.onload = async () => {
            try {
                const fileData = reader.result;
                const wb = XLSX.read(fileData, { type: 'binary' });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const rowObject: any[] = XLSX.utils.sheet_to_json(sheet);
                const newData = await formatData(rowObject);

                const goToCreate = () => {
                    dispatch(setReceivingDetailData(newData));
                    navigate('/receivings/new/');
                };

                if (newData.ESHOP_RECEIVING_ID) {
                    try {
                        const res = await api(
                            'get',
                            `api/secure/receivings/eshop-receiving-id/${encodeURIComponent(newData.ESHOP_RECEIVING_ID)}`
                        );
                        const apiReceiving: ReceivingType = res.data?.data ?? res.data;
                        if (apiReceiving?.ID) {
                            const merged = mergeReceivingFromExcel(apiReceiving, newData);
                            dispatch(setReceivingDetailData(merged));
                            navigate(`/receivings/${apiReceiving.ID}`, {
                                state: { prefilledDataFromImport: merged },
                            });
                        } else {
                            goToCreate();
                        }
                    } catch {
                        goToCreate();
                    }
                } else {
                    goToCreate();
                }
            } finally {
                setLoading(false);
            }
        };
    };

    const onUpdate = (files: FilesPropType) => {
        setLoading(true);
        files.map((file) => loadFileData(file));
    };

    const onDelete = (file: FileType) => {
        setInputFiles([]);
    };

    const dowlaodExcelPattern = () => {
        downloadFile(`/xlsx/import_receiving.xlsx`, 'importReceiving.xlsx');
    };

    return (
        <>
            <Typography component="div" gutterBottom>
                <span onClick={dowlaodExcelPattern} style={{ cursor: 'pointer' }}>
                    {translate('productsImport.excelFilePattern')}
                    <IconButton color="primary">
                        <DownloadIcon />
                    </IconButton>
                </span>
                <Tooltip title={translate('productsImport.columnsOptions')}>
                    <IconButton onClick={() => setShowColumnOptions((value) => !value)}>
                        <InfoIcon />
                    </IconButton>
                </Tooltip>
            </Typography>
            <form ref={formRef}>
                {loading && <Loading />}
                <FileUpload
                    files={inputFiles}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    accept=".xls, .xlsx"
                    height={150}
                    rawFiles
                    // displayFilesList
                    // disableShowFile
                    // disableDowloadFile
                    // customFileActions={customFileActions}
                />
                {showColumnOptions && (
                    <ColumnOptions
                        handlingUnitsCodes={handlingUnitsCodes}
                        receivingDeliveryTypeCodes={receivingDeliveryTypeCodes}
                        driverCompanyCodes={driverCompanyCodes}
                        receivingTypeCodes={receivingTypeCodes}
                        boolNumberCodes={boolNumberCodes}
                        incotermsCodes={incotermsCodes}
                        onClose={() => setShowColumnOptions(false)}
                    />
                )}
            </form>
        </>
    );
};
export default ReceivingImportContainer;
